export type SsoSite = string;
export type SsoEntryType = 'admin' | 'clinic' | 'unified';
export type PortalAuthTransport = 'bearer' | 'cookie';

export interface SsoContext {
  site: SsoSite;
  entryType: SsoEntryType;
}

export interface SsoLoginContext extends SsoContext {
  prompt: 'none';
}

export interface PortalAuthChannel extends SsoContext {
  transport: PortalAuthTransport;
}

type StorageReader = Pick<Storage, 'getItem'>;
type StorageWriter = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
type JsonRecord = Record<string, unknown>;

export interface SsoDenialVerdict {
  reason?: string;
  logoutUrl?: string;
}

const SSO_ENTRY_TYPES: readonly SsoEntryType[] = ['admin', 'clinic', 'unified'];
const SSO_MARKER_PREFIX = 'ra2n_sso_session';
const ACTIVE_PORTAL_ENTRY_PREFIX = 'ra2n_active_portal_entry';
const PORTAL_AUTH_TRANSPORT_PREFIX = 'ra2n_portal_auth_transport';
const SSO_DENIED_MARKER_PREFIX = 'ra2n_sso_denied';
const SSO_PASSIVE_MARKER_PREFIX = 'ra2n_sso_passive_check';
const SSO_PASSIVE_RETRY_MS = 15_000;
const SSO_REPAIR_MARKER_PREFIX = 'ra2n_sso_repair_attempt';
/**
 * Cửa sổ chống lặp cho việc "vá" phiên chung. Rộng hơn passive check vì một lần vá là một vòng
 * điều hướng TOÀN TRANG ra broker rồi quay lại — chậm hơn nhiều so với một lần probe.
 */
const SSO_REPAIR_RETRY_MS = 60_000;
const UNSAFE_METADATA_KEY = /(?:^|_)(?:access_?token|refresh_?token|token|password|credential|secret|authorization)(?:$|_)/i;

export const isSsoEnabled = (value: unknown): boolean => value === 'true';

export const isSsoSite = (value: string | undefined): value is SsoSite =>
  value !== undefined && /^[a-z0-9][a-z0-9-]{0,63}$/.test(value);

const assertContext = (context: SsoContext): void => {
  if (!isSsoSite(context.site) || !SSO_ENTRY_TYPES.includes(context.entryType)) {
    throw new Error('Invalid P1 SSO context.');
  }
};

const passiveMarkerKey = (context: SsoContext): string => {
  assertContext(context);
  return `${SSO_PASSIVE_MARKER_PREFIX}:${context.site}:${context.entryType}`;
};

export const shouldStartPassiveSsoCheck = ({
  context,
  passiveSsoEnabled,
  hasLocalCredential,
  manualLoginActive = false,
  search,
  storage,
  now = Date.now(),
}: {
  context: SsoContext;
  // Accepted for backward-compat but no longer gates passive detection: the passive check depends
  // ONLY on the passive flag so the SSO button can stay hidden (VITE_SSO_ENABLED=false) while
  // cross-Portal detection is ON (VITE_SSO_PASSIVE_ENABLED=true).
  ssoEnabled?: unknown;
  passiveSsoEnabled: unknown;
  hasLocalCredential: boolean;
  manualLoginActive?: boolean;
  search: string;
  storage: StorageWriter;
  now?: number;
}): boolean => {
  if (
    !isSsoEnabled(passiveSsoEnabled) ||
    hasLocalCredential ||
    manualLoginActive
  ) {
    return false;
  }

  if (new URLSearchParams(search).get('sso_passive') === 'checked') {
    storage.removeItem(passiveMarkerKey(context));
    return false;
  }

  const previousValue = storage.getItem(passiveMarkerKey(context));
  const previousCheck = previousValue === null ? Number.NaN : Number(previousValue);

  return !(
    Number.isFinite(previousCheck) &&
    now - previousCheck < SSO_PASSIVE_RETRY_MS
  );
};

export const shouldNavigateAfterPortalLogin = (
  establishUrl: unknown,
): boolean => typeof establishUrl !== 'string' || establishUrl.trim() === '';

const repairMarkerKey = (context: SsoContext): string => {
  assertContext(context);
  return `${SSO_REPAIR_MARKER_PREFIX}:${context.site}:${context.entryType}`;
};

/**
 * Có được phép điều hướng ra broker để vá lại phiên chung lần này không.
 *
 * VÌ SAO PHẢI CHẶN. `GET /admin/auth/user` trả `sso_establish_url` mỗi khi
 * `AdminSiteAuthController::brokerRepairUrl()` thấy token còn ràng buộc broker, và
 * SiteAdminAuthContext điều hướng TOÀN TRANG tới đó. Chỗ đó chỉ kiểm tra "có phải chuỗi rỗng
 * không" (`shouldNavigateAfterPortalLogin`), không đếm số lần. Nên nếu chuyến vá không thật sự
 * khôi phục được phiên chung — ví dụ SLO ở portal khác vừa xoá cookie `medix_sso_sid` — thì:
 *
 *     /admin/auth/user -> establish_url -> điều hướng ra broker -> quay lại
 *       -> /admin/auth/user -> establish_url -> ... lặp vô hạn
 *
 * Người dùng thấy màn hình treo ở trạng thái loading, toast 「トークンが切れました」 nhấp nháy, và
 * trang tự tải lại mãi. Đây là hệ quả mới xuất hiện KỂ TỪ KHI SLO chạy thật: trước đó back-channel
 * không bao giờ thu hồi phiên chung nên vòng vá này không có cớ để lặp.
 *
 * Một lần vá trong mỗi cửa sổ là đủ: vá được thì lần sau server không trả URL nữa; vá không được
 * thì thà để người dùng ở màn đăng nhập còn hơn treo máy.
 */
export const shouldAttemptBrokerSessionRepair = ({
  context,
  establishUrl,
  storage,
  now = Date.now(),
}: {
  context: SsoContext;
  establishUrl: unknown;
  storage: StorageWriter;
  now?: number;
}): boolean => {
  if (shouldNavigateAfterPortalLogin(establishUrl)) {
    return false;
  }

  const previousValue = storage.getItem(repairMarkerKey(context));
  const previousAttempt = previousValue === null ? Number.NaN : Number(previousValue);

  return !(
    Number.isFinite(previousAttempt) &&
    now - previousAttempt < SSO_REPAIR_RETRY_MS
  );
};

export const markBrokerSessionRepairAttempt = (
  context: SsoContext,
  storage: StorageWriter,
  now: number = Date.now(),
): void => {
  storage.setItem(repairMarkerKey(context), String(now));
};

/** Vá xong và phiên đã dùng được thì xoá dấu, để lần hỏng sau vẫn được thử lại ngay. */
export const clearBrokerSessionRepairAttempt = (
  context: SsoContext,
  storage: StorageWriter,
): void => {
  storage.removeItem(repairMarkerKey(context));
};

export const selectPostLoginNavigation = (
  establishUrl: unknown,
  localPath: string,
): { kind: 'external' | 'local'; url: string } => {
  if (!shouldNavigateAfterPortalLogin(establishUrl)) {
    return { kind: 'external', url: (establishUrl as string).trim() };
  }

  return { kind: 'local', url: localPath };
};

export const shouldShowPassiveSsoSplash = ({
  canStartPassiveSsoCheck,
  passiveRedirectStarted,
}: {
  canStartPassiveSsoCheck: boolean;
  passiveRedirectStarted: boolean;
}): boolean => canStartPassiveSsoCheck || passiveRedirectStarted;

export const markPassiveSsoCheck = (
  context: SsoContext,
  storage: Pick<Storage, 'setItem'>,
  now: number = Date.now(),
): void => {
  storage.setItem(passiveMarkerKey(context), String(now));
};

/**
 * Removes the one-shot `sso_passive=checked` marker from the address bar after a
 * passive check resolved to "no central session". The gate keeps this page on
 * the login form (so local login still works), but stripping the param means a
 * later reload starts clean and re-attempts passive SSO — e.g. once the user has
 * signed in on another Portal. Uses replaceState so it does not re-trigger the
 * current page's passive decision.
 */
export const stripPassiveMarkerFromUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get('sso_passive') !== 'checked') {
    return;
  }
  url.searchParams.delete('sso_passive');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

const normalizedApiOrigin = (apiBaseUrl: string): string => {
  const url = new URL(apiBaseUrl);

  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username !== '' ||
    url.password !== '' ||
    url.search !== '' ||
    url.hash !== '' ||
    !['', '/'].includes(url.pathname)
  ) {
    throw new Error('The P1 API base URL must be an HTTP(S) origin.');
  }

  return url.origin;
};

export const buildSsoLoginUrl = (
  apiBaseUrl: string,
  context: SsoLoginContext,
): string => {
  assertContext(context);

  const url = new URL(
    `/api/v1/sso/${context.site}/login`,
    normalizedApiOrigin(apiBaseUrl),
  );
  url.searchParams.set('entry_type', context.entryType);

  url.searchParams.set('prompt', context.prompt);

  return url.toString();
};

export const ssoReturnPath = (context: SsoContext): string => {
  assertContext(context);

  switch (context.entryType) {
    case 'admin':
      return `/${context.site}/admin`;
    case 'clinic':
    case 'unified':
      return `/${context.site}`;
  }
};

export const ssoCompletionPath = (context: SsoContext): string =>
  `${ssoReturnPath(context)}/sso/complete`;

export const ssoLoginPath = (context: SsoContext): string =>
  `${ssoReturnPath(context)}/login`;

export const ssoUserEndpoint = (context: SsoContext): string => {
  assertContext(context);

  switch (context.entryType) {
    case 'admin':
      return `/admin/${context.site}/user`;
    case 'clinic':
      return `/clinic/${context.site}/auth/user`;
    case 'unified':
      return `/portal/${context.site}/me/session`;
  }
};

export const claimPassiveSsoCheck = (
  context: SsoContext,
  search: string,
  hasLocalCredential: boolean,
  storage: StorageWriter,
  now: number = Date.now(),
): boolean => {
  if (!shouldStartPassiveSsoCheck({
    context,
    ssoEnabled: 'true',
    passiveSsoEnabled: 'true',
    hasLocalCredential,
    search,
    storage,
    now,
  })) {
    return false;
  }

  markPassiveSsoCheck(context, storage, now);
  return true;
};

const markerKey = (context: SsoContext): string => {
  assertContext(context);
  return `${SSO_MARKER_PREFIX}:${context.site}:${context.entryType}`;
};

const browserSessionStorage = (): Storage | undefined =>
  typeof window === 'undefined' ? undefined : window.sessionStorage;

export const hasSsoSession = (
  context: SsoContext,
  storage: StorageReader | undefined = browserSessionStorage(),
): boolean => storage?.getItem(markerKey(context)) === '1';

export const markSsoSession = (
  context: SsoContext,
  storage: StorageWriter | undefined = browserSessionStorage(),
): void => {
  storage?.setItem(markerKey(context), '1');
};

export const clearSsoSession = (
  context: SsoContext,
  storage: StorageWriter | undefined = browserSessionStorage(),
): void => {
  storage?.removeItem(markerKey(context));
};

const activePortalEntryKey = (site: SsoSite): string => {
  if (!isSsoSite(site)) {
    throw new Error('Invalid P1 SSO site.');
  }
  return `${ACTIVE_PORTAL_ENTRY_PREFIX}:${site}`;
};

const portalAuthTransportKey = (context: SsoContext): string => {
  assertContext(context);
  return `${PORTAL_AUTH_TRANSPORT_PREFIX}:${context.site}:${context.entryType}`;
};

/**
 * Non-secret browser routing metadata for tenants that render multiple legacy
 * principal types in one shell. The backend still authenticates the exact
 * cookie/token and principal class; this value only chooses which protected
 * endpoint to ask. Missing or malformed metadata never grants access.
 */
export const readActivePortalEntry = (
  site: SsoSite,
  storage: StorageReader,
): SsoEntryType | null => {
  const value = storage.getItem(activePortalEntryKey(site));
  return SSO_ENTRY_TYPES.includes(value as SsoEntryType)
    ? (value as SsoEntryType)
    : null;
};

export const markActivePortalEntry = (
  context: SsoContext,
  storage: Pick<Storage, 'setItem'>,
): void => {
  assertContext(context);
  storage.setItem(activePortalEntryKey(context.site), context.entryType);
};

export const clearActivePortalEntry = (
  context: SsoContext,
  storage: StorageWriter,
): void => {
  assertContext(context);
  if (readActivePortalEntry(context.site, storage) === context.entryType) {
    storage.removeItem(activePortalEntryKey(context.site));
  }
};

export const readPortalAuthTransport = (
  context: SsoContext,
  storage: StorageReader,
): PortalAuthTransport | null => {
  const value = storage.getItem(portalAuthTransportKey(context));
  return value === 'bearer' || value === 'cookie' ? value : null;
};

/**
 * Persists only non-secret routing metadata. The value says which exact browser
 * credential channel backs this site/entry pair; it never contains a token or
 * cookie value.
 */
export const markPortalAuthChannel = (
  context: SsoContext,
  transport: PortalAuthTransport,
  storage: Pick<Storage, 'setItem'>,
): void => {
  assertContext(context);
  storage.setItem(portalAuthTransportKey(context), transport);
  markActivePortalEntry(context, storage);
};

export const clearPortalAuthChannel = (
  context: SsoContext,
  storage: StorageWriter,
): void => {
  storage.removeItem(portalAuthTransportKey(context));
  clearActivePortalEntry(context, storage);
};

export const readActivePortalAuthChannel = (
  site: SsoSite,
  storage: StorageReader,
): PortalAuthChannel | null => {
  const entryType = readActivePortalEntry(site, storage);
  if (entryType === null) {
    return null;
  }

  const context = { site, entryType } as const;
  const transport = readPortalAuthTransport(context, storage);
  return transport === null ? null : { ...context, transport };
};

/**
 * One-time, idempotent browser cutover for a unified tenant. Split-channel
 * credentials are never interpreted as unified sessions; users sign in once
 * again after cutover, as required by the SSO bundle transition contract.
 */
export const normalizeUnifiedPortalStorage = (
  site: SsoSite,
  localStorage: StorageWriter,
  sessionStorage: StorageWriter,
): void => {
  const unified = { site, entryType: 'unified' } as const;
  assertContext(unified);
  const activeEntry = readActivePortalEntry(site, localStorage);
  const unifiedTransport = activeEntry === 'unified'
    ? readPortalAuthTransport(unified, localStorage)
    : null;

  [
    'site_admin_token', 'site_admin', 'site_admin_site',
    'massager_token', 'massager_user', 'massager_clinic', 'massager_site',
  ].forEach(key => localStorage.removeItem(key));

  for (const legacyEntry of ['admin', 'clinic'] as const) {
    const legacy = { site, entryType: legacyEntry } as const;
    localStorage.removeItem(portalAuthTransportKey(legacy));
    sessionStorage.removeItem(markerKey(legacy));
    sessionStorage.removeItem(passiveMarkerKey(legacy));
    sessionStorage.removeItem(deniedMarkerKey(legacy));
  }

  if (activeEntry !== 'unified') {
    localStorage.removeItem(activePortalEntryKey(site));
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_clinic');
    sessionStorage.removeItem(markerKey(unified));
    return;
  }

  if (unifiedTransport === 'cookie') {
    localStorage.removeItem('auth_token');
  }
};

/**
 * Detects an HttpOnly Portal session across both generations of browser
 * metadata: the legacy tab marker and the persisted non-secret channel used by
 * a newly opened tab.
 */
export const hasPortalCookieSession = (
  context: SsoContext,
  persistentStorage: StorageReader,
  tabStorage: StorageReader,
): boolean => {
  const channel = readActivePortalAuthChannel(context.site, persistentStorage);
  return hasSsoSession(context, tabStorage) ||
    (channel?.entryType === context.entryType && channel.transport === 'cookie');
};

const portalBearerCredentialKey = (entryType: SsoEntryType): string =>
  entryType === 'admin' ? 'site_admin_token' : 'auth_token';

/**
 * Selects an ambient bearer only for the exact active portal channel. A cookie
 * channel deliberately suppresses stale bearer values left by another session.
 * The metadata-free fallback preserves pre-cutover legacy sessions while still
 * selecting by the request's exact entry type instead of by whichever token exists.
 */
export const selectExactPortalBearerToken = (
  context: SsoContext,
  storage: StorageReader,
): string | null => {
  assertContext(context);
  const activeEntry = readActivePortalEntry(context.site, storage);
  if (activeEntry !== null && activeEntry !== context.entryType) {
    return null;
  }

  const transport = readPortalAuthTransport(context, storage);
  if (transport === 'cookie') {
    return null;
  }

  const token = storage.getItem(portalBearerCredentialKey(context.entryType))?.trim();
  return token ? token : null;
};

const deniedMarkerKey = (context: SsoContext): string => {
  assertContext(context);
  return `${SSO_DENIED_MARKER_PREFIX}:${context.site}:${context.entryType}`;
};

/**
 * Distinguishes an authoritative "no access on this Portal" answer from a technical fault.
 *
 * Only an explicit HTTP 403 counts. A 401, a timeout, a CORS/network fault or an offline
 * browser must keep surfacing the generic failure screen: dressing an outage up as an
 * access decision would tell the user to give up when the right advice is to retry.
 */
export const isSsoAccessDeniedError = (error: unknown): boolean =>
  isRecord(error) && isRecord(error.response) && error.response.status === 403;

/**
 * Remembers, for this tab and Portal context, that SSO already answered "this identity has
 * no account here".
 *
 * The completion page stays reachable after the broker's one-shot completion cookie is gone
 * (a reload once it expired, back/forward, a bookmarked completion URL). That second visit
 * has no server verdict left to read, and the fallback probe can only report "unauthenticated",
 * which used to render as 「SSOログインを完了できませんでした。」 — an outage message that
 * invites the user to keep retrying a login that can never succeed. Session-scoped on purpose:
 * every later authoritative completion overwrites or clears it, so a mapping added in the
 * meantime converges on the next round trip.
 */
export const rememberSsoDenial = (
  context: SsoContext,
  verdict: SsoDenialVerdict,
  storage: StorageWriter | undefined = browserSessionStorage(),
): void => {
  const payload: SsoDenialVerdict = {};
  if (typeof verdict.reason === 'string') {
    payload.reason = verdict.reason;
  }
  if (typeof verdict.logoutUrl === 'string') {
    payload.logoutUrl = verdict.logoutUrl;
  }
  storage?.setItem(deniedMarkerKey(context), JSON.stringify(payload));
};

export const readRememberedSsoDenial = (
  context: SsoContext,
  storage: StorageReader | undefined = browserSessionStorage(),
): SsoDenialVerdict | undefined => {
  const stored = storage?.getItem(deniedMarkerKey(context));

  if (typeof stored !== 'string') {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!isRecord(parsed)) {
      return undefined;
    }

    const verdict: SsoDenialVerdict = {};
    if (typeof parsed.reason === 'string') {
      verdict.reason = parsed.reason;
    }
    // Never trusted as a link on its own: the completion page re-validates it against the
    // exact broker-logout shape before it is allowed to become an anchor href.
    if (typeof parsed.logoutUrl === 'string') {
      verdict.logoutUrl = parsed.logoutUrl;
    }
    return verdict;
  } catch {
    return undefined;
  }
};

export const clearSsoDenial = (
  context: SsoContext,
  storage: StorageWriter | undefined = browserSessionStorage(),
): void => {
  storage?.removeItem(deniedMarkerKey(context));
};

export const endSsoSession = async (
  context: SsoContext,
  revokeSession: (
    endpoint: string,
    payload: { entry_type: SsoEntryType },
  ) => Promise<unknown>,
  _storage: StorageWriter | undefined = browserSessionStorage(),
): Promise<string> => {
  assertContext(context);
  const payload = await revokeSession(
    `/sso/${context.site}/logout`,
    { entry_type: context.entryType },
  );

  if (!isRecord(payload) || typeof payload.logout_url !== 'string') {
    throw new Error('P1 SSO logout response is invalid.');
  }

  const logoutUrl = validateSsoLogoutUrl(payload.logout_url, context);
  if (logoutUrl === undefined) {
    throw new Error('P1 SSO logout response is invalid.');
  }

  return logoutUrl;
};

export const validateSsoLogoutUrl = (
  value: unknown,
  context?: SsoContext,
  portalOrigin: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.origin,
): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    throw new Error('P1 SSO logout URL is invalid.');
  }

  let logoutUrl: URL;
  try {
    logoutUrl = new URL(value);
  } catch {
    throw new Error('P1 SSO logout URL is invalid.');
  }

  const isExactPortalLogin = context !== undefined && portalOrigin !== undefined
    ? isExactPortalLoginUrl(logoutUrl, context, portalOrigin)
    : false;
  const isCognitoLogout =
    logoutUrl.pathname === '/logout' &&
    logoutUrl.searchParams.has('client_id') &&
    logoutUrl.searchParams.has('logout_uri');
  const isBrokerLogout = isValidatedBrokerLogoutUrl(logoutUrl, portalOrigin);

  if (
    logoutUrl.username !== '' ||
    logoutUrl.password !== '' ||
    logoutUrl.hash !== '' ||
    (context !== undefined
      ? !isExactPortalLogin
      : !schemeAllowedForPage(logoutUrl, portalOrigin) || (!isCognitoLogout && !isBrokerLogout))
  ) {
    throw new Error('P1 SSO logout URL is invalid.');
  }

  return logoutUrl.toString();
};

export const validateDeniedBrokerLogoutUrl = (
  value: unknown,
  context: SsoContext,
  portalOrigin: string | undefined = typeof window === 'undefined'
    ? undefined
    : window.location.origin,
): string | undefined => {
  if (typeof value !== 'string' || portalOrigin === undefined) {
    return undefined;
  }

  try {
    const logoutUrl = new URL(value);
    if (!schemeAllowedForPage(logoutUrl, portalOrigin)
      || logoutUrl.username !== ''
      || logoutUrl.password !== ''
      || logoutUrl.hash !== ''
      || logoutUrl.pathname !== '/sso-broker/logout'
      || [...logoutUrl.searchParams].length !== 1) {
      return undefined;
    }

    const returnTo = new URL(logoutUrl.searchParams.get('return_to') ?? '');
    return isExactPortalLoginUrl(returnTo, context, portalOrigin)
      ? logoutUrl.toString()
      : undefined;
  } catch {
    return undefined;
  }
};

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Whether a URL's scheme is acceptable for the page doing the asking.
 *
 * An https page must NEVER be sent to an http URL -- that is a downgrade. A page already served
 * over http may be: an internal deployment reached by IP:port has no certificate, and every one of
 * these guards used to hard-require https, which broke SSO there SILENTLY. Three separate symptoms
 * traced back to exactly this on dev3 (2026-08-28): P3 never established a shared session
 * (resolveSsoEstablishUrl, fixed first), active session sync dropped its own redirect
 * (safeActiveSessionSyncAbsoluteUrl), and the 403 screen could not render its 「SSOからログアウト」
 * button at all, leaving the operator stuck on a dead end with no way back to a login form.
 *
 * `pageOrigin` is passed where the caller already has it so this stays testable without a DOM.
 */
const schemeAllowedForPage = (url: URL, pageOrigin?: string): boolean => {
  if (url.protocol === 'https:') {
    return true;
  }
  if (url.protocol !== 'http:') {
    return false;
  }

  const origin = pageOrigin
    ?? (typeof window === 'undefined' ? undefined : window.location.origin);
  if (origin === undefined) {
    return false;
  }

  try {
    return new URL(origin).protocol === 'http:';
  } catch {
    return false;
  }
};

const isValidatedBrokerLogoutUrl = (logoutUrl: URL, pageOrigin?: string): boolean => {
  if (logoutUrl.pathname !== '/sso-broker/logout'
    || [...logoutUrl.searchParams].length !== 1
    || !logoutUrl.searchParams.has('return_to')) {
    return false;
  }

  try {
    const returnTo = new URL(logoutUrl.searchParams.get('return_to') ?? '');

    return schemeAllowedForPage(returnTo, pageOrigin)
      && returnTo.username === ''
      && returnTo.password === ''
      && returnTo.hash === ''
      && returnTo.search === '';
  } catch {
    return false;
  }
};

const assertSafeMetadata = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(assertSafeMetadata);
    return;
  }
  if (!isRecord(value)) {
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    if (UNSAFE_METADATA_KEY.test(key)) {
      throw new Error('Credential-like data cannot be persisted for an SSO session.');
    }
    assertSafeMetadata(child);
  }
};

const requiredMetadata = (payload: JsonRecord, key: string): JsonRecord => {
  const value = payload[key];
  if (!isRecord(value)) {
    throw new Error(`P1 SSO completion payload is missing ${key} metadata.`);
  }
  assertSafeMetadata(value);
  return value;
};

const clearPortalCredential = (
  context: SsoContext,
  localStorage: StorageWriter,
): void => {
  localStorage.removeItem(portalBearerCredentialKey(context.entryType));
};

const isExactPortalLoginUrl = (
  logoutUrl: URL,
  context: SsoContext,
  portalOrigin: string,
): boolean => {
  let expectedOrigin: URL;
  try {
    expectedOrigin = new URL(portalOrigin);
  } catch {
    return false;
  }

  return ['http:', 'https:'].includes(expectedOrigin.protocol)
    && expectedOrigin.origin === portalOrigin
    && expectedOrigin.pathname === '/'
    && expectedOrigin.search === ''
    && expectedOrigin.hash === ''
    && logoutUrl.origin === expectedOrigin.origin
    && logoutUrl.pathname === ssoLoginPath(context)
    && logoutUrl.search === '';
};

export const clearPortalSession = (
  context: SsoContext,
  localStorage: StorageWriter,
  sessionStorage: StorageWriter,
): void => {
  assertContext(context);
  const metadataKeys = {
    admin: ['site_admin_token', 'site_admin', 'site_admin_site'],
    clinic: ['auth_token', 'auth_user', 'auth_clinic'],
    unified: [
      'auth_token', 'auth_user', 'auth_clinic',
      'site_admin_token', 'site_admin', 'site_admin_site',
      'massager_token', 'massager_user', 'massager_clinic', 'massager_site',
    ],
  }[context.entryType];
  metadataKeys.forEach(key => localStorage.removeItem(key));
  clearPortalAuthChannel(context, localStorage);
  clearSsoSession(context, sessionStorage);
  // The login page that follows a sign-out must probe again, whatever the 15 s throttle says:
  // its previous probe's challenge is bound to the broker session the browser held THEN, and a
  // login submitted on that stale challenge after the shared session changed is abandoned by the
  // broker at /sso-broker/establish (dev1 06/09/2026: a P1 re-login within seconds of a P1
  // logout, with 開示 signed in meanwhile, died in the same second it was created).
  sessionStorage.removeItem(passiveMarkerKey(context));
};

export const persistSsoCompletion = (
  context: SsoContext,
  payload: unknown,
  localStorage: StorageWriter,
  sessionStorage: StorageWriter,
): void => {
  assertContext(context);
  if (!isRecord(payload)) {
    throw new Error('P1 SSO completion payload is invalid.');
  }

  clearPortalCredential(context, localStorage);

  if (context.entryType === 'unified') {
    [
      'site_admin_token', 'site_admin', 'site_admin_site',
      'massager_token', 'massager_user', 'massager_clinic', 'massager_site',
    ].forEach(key => localStorage.removeItem(key));
  }

  switch (context.entryType) {
    case 'admin': {
      const admin = requiredMetadata(payload, 'admin');
      localStorage.setItem('site_admin', JSON.stringify(admin));
      localStorage.setItem('site_admin_site', context.site);
      break;
    }
    case 'clinic':
    case 'unified': {
      const user = requiredMetadata(payload, 'user');
      const clinic = requiredMetadata(payload, 'clinic');
      localStorage.setItem('auth_user', JSON.stringify(user));
      localStorage.setItem('auth_clinic', JSON.stringify(clinic));
      break;
    }
  }

  markPortalAuthChannel(context, 'cookie', localStorage);
  markSsoSession(context, sessionStorage);
};

export const completeSsoSession = async (
  context: SsoContext,
  loadSessionMetadata: (endpoint: string) => Promise<unknown>,
  localStorage: StorageWriter,
  sessionStorage: StorageWriter,
): Promise<string> => {
  const payload = await loadSessionMetadata(ssoUserEndpoint(context));
  persistSsoCompletion(context, payload, localStorage, sessionStorage);
  return ssoReturnPath(context);
};
